/**
 * The API envelope.
 *
 * Sections 5.9 and 5.10: every response is either
 *   { success: true,  data, pagination? }
 * or
 *   { success: false, error: { code, message, details? } }
 *
 * Section 5.11: the HTTP status code carries meaning — 404 not found, 409 conflict,
 * 422 validation. Never 200-for-everything.
 */
// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------
/**
 * A closed set, so the frontend can branch on `error.code` instead of matching on
 * message text. Add a code here before using it in a route.
 */
export const API_ERROR_CODE = {
    /** 422 — the request did not satisfy the schema or a field-level rule. */
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    /** 401 — no signed-in account, on a route that needs one (D21). */
    UNAUTHORIZED: 'UNAUTHORIZED',
    /** 403 — signed in, but the account does not hold the required role (D21). */
    FORBIDDEN: 'FORBIDDEN',
    /** 404 — the addressed record does not exist. */
    NOT_FOUND: 'NOT_FOUND',
    /** 409 — the request contradicts the current state of the data. */
    CONFLICT: 'CONFLICT',
    /** 409 — a status move the lifecycle forbids (Section 8.31, Plan D2). */
    INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',
    /** 409 — the record already exists, e.g. a duplicate assignment (Section 4.22). */
    DUPLICATE: 'DUPLICATE',
    /** 409 — activity times overlap; retry with `allow_overlap` (Plan D4). */
    OVERLAP_DETECTED: 'OVERLAP_DETECTED',
    /** 409 — no assignment exists; retry with `allow_unassigned` (Plan D3). */
    ASSIGNMENT_REQUIRED: 'ASSIGNMENT_REQUIRED',
    /** 429 — public registration rate limit (Sections 1.7, 9.31). */
    RATE_LIMITED: 'RATE_LIMITED',
    /** 413 — request body over the cap on the public endpoint. */
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    /** 500 — anything unhandled. Never leaks a stack trace to the client. */
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    /** 503 — a dependency (the database) is unavailable. */
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};
/** The HTTP status each code is served with. One mapping, used by the error handler. */
export const API_ERROR_STATUS = {
    VALIDATION_ERROR: 422,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    INVALID_STATE_TRANSITION: 409,
    DUPLICATE: 409,
    OVERLAP_DETECTED: 409,
    ASSIGNMENT_REQUIRED: 409,
    RATE_LIMITED: 429,
    PAYLOAD_TOO_LARGE: 413,
    INTERNAL_ERROR: 500,
    SERVICE_UNAVAILABLE: 503,
};
// ---------------------------------------------------------------------------
// Common query parameters
// ---------------------------------------------------------------------------
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;
// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------
export function isApiSuccess(response) {
    return response.success === true;
}
export function isApiFailure(response) {
    return response.success === false;
}
//# sourceMappingURL=api.js.map